const resolveApiBase = (): string => {
  const runtimeBase =
    (typeof window !== 'undefined' && (window as any).__API_BASE__)
      ? String((window as any).__API_BASE__)
      : '';
  if (runtimeBase.trim()) return runtimeBase.replace(/\/+$/, '');

  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = window.location.hostname || '';
    const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
    if (!isLocalHost) {
      return `${window.location.origin.replace(/\/+$/, '')}/api`;
    }
  }

  const envBase =
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE)
      ? String(import.meta.env.VITE_API_BASE)
      : '';
  if (envBase.trim()) return envBase.replace(/\/+$/, '');

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin.replace(/\/+$/, '')}/api`;
  }

  return '/api';
};

const API_BASE = resolveApiBase();
export const assetUrl = (url: string | null | undefined): string => {
  const PLACEHOLDER = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=1200&auto=format&fit=crop';
  if (!url) return PLACEHOLDER;
  const origin = API_BASE.replace(/\/api$/, '');
  const host = typeof window !== 'undefined' ? (window.location.hostname || '') : '';
  const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  const uploadsBase = isLocalHost ? origin : API_BASE;
  if (/^https?:\/\//.test(url)) {
    if (url.startsWith('https://images.unsplash.com')) {
      return '/unsplash' + url.replace('https://images.unsplash.com', '');
    }
    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/uploads/') || parsed.pathname.startsWith('/api/uploads/')) {
        if (parsed.pathname.startsWith('/api/uploads/')) return origin + parsed.pathname;
        return uploadsBase + parsed.pathname;
      }
    } catch {
      // keep original URL if parsing fails
    }
    return url;
  }
  if (url.startsWith('/uploads')) {
    return uploadsBase + url;
  }
  if (url.startsWith('/api/uploads')) {
    return origin + url;
  }
  if (url.startsWith('uploads/')) return uploadsBase + '/' + url;
  if (url.startsWith('api/uploads/')) return origin + '/' + url;
  if (url.startsWith('/')) return origin + url;
  return origin + '/' + url;
};

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  const mergedHeaders: Record<string, string> = {
    ...baseHeaders,
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) { mergedHeaders.Authorization = `Bearer ${token}`; }

  console.log(`📡 API ${options.method || 'GET'} ${endpoint}`);
  
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: mergedHeaders,
      cache: options.cache ?? 'no-store',
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('⚠️ Unauthorized (401). Clearing token and redirecting...');
        localStorage.removeItem('token');
        // Dispatch logout event so the app can react without reloading
        window.dispatchEvent(new Event('auth:logout'));
        throw new Error('Session expired. Please login again.');
      }
      const errorText = await response.text();
      console.error(`❌ API Error ${response.status}:`, errorText);
      throw new Error(`API Error ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log(`✅ API Success: ${endpoint}`, data);
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error(`❌ Network Error for ${endpoint}:`, msg);
    throw error;
  }
};

// Hero API
export const heroApi = {
  getAll: () => apiRequest('/hero'),
  getById: (id: string) => apiRequest(`/hero/${id}`),
  create: (data: unknown) => apiRequest('/hero', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: unknown) => apiRequest(`/hero/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest(`/hero/${id}`, {
    method: 'DELETE',
  }),
  uploadImage: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/hero/${id}/upload`, {
      method: 'POST',
      headers,
      body: formData
    });
    return await res.json();
  },
  uploadVideo: (id: string, file: File) => {
    const form = new FormData();
    form.append('video', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/hero/${id}/video/upload`, { method: 'POST', body: form, headers }).then(r => r.json());
  },
  updateVideoSettings: (id: string, settings: unknown, use_video?: boolean) => apiRequest(`/hero/${id}/video/settings`, {
    method: 'PUT',
    body: JSON.stringify({ settings, use_video })
  }),
  toggleUseVideo: (id: string, use_video: boolean) => apiRequest(`/hero/${id}/use_video`, {
    method: 'PUT',
    body: JSON.stringify({ use_video })
  }),
};

// About API
export const aboutApi = {
  getAll: (includeInactive: boolean = false) => apiRequest(`/about${includeInactive ? '?includeInactive=true' : ''}`),
  getById: (id: number) => apiRequest(`/about/${id}`),
  create: (data: unknown) => apiRequest('/about', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest(`/about/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/about/${id}`, {
    method: 'DELETE',
  }),
  getSettings: () => apiRequest('/about/settings'),
  updateSettings: (data: { model_source?: 'default'|'custom'; component_type?: string; model_url?: string|null; animation_settings?: unknown }) => apiRequest('/about/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  uploadImage: (id: number, formData: FormData) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/about/${id}/upload`, {
      method: 'POST',
      body: formData,
      headers
    }).then(res => res.json());
  },
  uploadIcon: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/about/upload-icon`, {
      method: 'POST',
      headers,
      body: formData
    });
    return await res.json();
  },
  uploadModel: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/about/upload-model`, {
      method: 'POST',
      headers,
      body: formData
    });
    return await res.json();
  },
  uploadAsset: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/about/upload-asset`, {
      method: 'POST',
      headers,
      body: formData
    });
    return await res.json();
  }
};

// About Extended API for Mission/Vision, Core Values, Why Choose
export const aboutExtendedApi = {
  getMissionVision: () => apiRequest('/about/about-extended/mission-vision'),
  updateMissionVision: (data: { mission_title?: string; mission_description?: string; vision_title?: string; vision_description?: string }) => apiRequest('/about/about-extended/mission-vision', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getCoreValues: () => apiRequest('/about/about-extended/core-values'),
  updateCoreValues: (data: { values: any[] }) => apiRequest('/about/about-extended/core-values', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getWhyChoose: () => apiRequest('/about/about-extended/why-choose'),
  updateWhyChoose: (data: { items: any[] }) => apiRequest('/about/about-extended/why-choose', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Services API
export const serviceApi = {
  getAll: (active: boolean = true) => apiRequest(`/services${active === false ? '?active=false' : ''}`),
  getById: (id: number) => apiRequest(`/services/${id}`),
  getBySlug: (slug: string) => apiRequest(`/services/slug/${slug}`),
  create: (data: unknown) => apiRequest('/services', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest(`/services/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/services/${id}`, {
    method: 'DELETE',
  }),
  uploadImage: (id: number, formData: FormData) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/services/${id}/upload`, {
      method: 'POST',
      body: formData,
      headers
    }).then(res => res.json());
  },
  uploadGallery: (id: number, formData: FormData) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/services/${id}/gallery`, {
      method: 'POST',
      body: formData,
      headers
    }).then(res => res.json());
  },
  deleteGalleryImage: (id: number, imageUrl: string) => apiRequest(`/services/${id}/gallery`, {
    method: 'DELETE',
    body: JSON.stringify({ imageUrl })
  }),
  setThumbnail: (id: number, imageUrl: string) => apiRequest(`/services/${id}/thumbnail`, {
    method: 'PUT',
    body: JSON.stringify({ imageUrl })
  }),
  uploadIcon: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/services/upload-icon`, {
      method: 'POST',
      headers,
      body: formData
    });
    return await res.json();
  },
};

export const serviceCategoryApi = {
  getAll: (active: boolean = true) => apiRequest(`/service-categories${active === false ? '?active=false' : ''}`),
  create: (data: unknown) => apiRequest('/service-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest(`/service-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/service-categories/${id}`, {
    method: 'DELETE',
  }),
};

export const serviceContentApi = {
  get: () => apiRequest('/services-content'),
  update: (data: {
    badge_text?: string;
    heading?: string;
    description?: string;
    stat1_label?: string;
    stat1_value?: string;
    stat2_label?: string;
    stat2_value?: string;
    stat3_label?: string;
    stat3_value?: string;
    cta_heading?: string;
    cta_description?: string;
    button_text?: string;
    button_action?: 'modal' | 'link';
    button_link?: string | null;
    secondary_button_text?: string;
    secondary_button_action?: 'modal' | 'link';
    secondary_button_link?: string | null;
  }) => apiRequest('/services-content', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

export const aboutPageContentApi = {
  get: () => apiRequest('/about-page-content'),
  update: (data: {
    cta_heading?: string;
    cta_description?: string;
    button_text?: string;
    button_action?: 'modal' | 'link';
    button_link?: string | null;
  }) => apiRequest('/about-page-content', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Projects API (Portfolio)
export const projectApi = {
  getAll: () => apiRequest('/projects'),
  getById: (id: number) => apiRequest(`/projects/${id}`),
  getBySlug: (slug: string) => apiRequest(`/projects/slug/${slug}`),
  getFeatured: () => apiRequest('/projects/featured'),
  create: (data: unknown) => apiRequest('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/projects/${id}`, {
    method: 'DELETE',
  }),
  uploadImage: (id: number, formData: FormData) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/projects/${id}/upload`, {
      method: 'POST',
      body: formData,
      headers
    }).then(res => res.json());
  },
  uploadGallery: (id: number, formData: FormData) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/projects/${id}/gallery`, {
      method: 'POST',
      body: formData,
      headers
    }).then(res => res.json());
  },
  deleteGalleryImage: (id: number, imageUrl: string) => apiRequest(`/projects/${id}/gallery`, {
    method: 'DELETE',
    body: JSON.stringify({ imageUrl })
  }),
  setThumbnail: (id: number, imageUrl: string) => apiRequest(`/projects/${id}/thumbnail`, {
    method: 'PUT',
    body: JSON.stringify({ imageUrl })
  }),
  incrementViews: (id: number) => apiRequest(`/projects/${id}/views`, { method: 'POST' })
};

// Project Categories API
export const projectCategoryApi = {
  getAll: () => apiRequest('/project-categories'),
  create: (data: unknown) => apiRequest('/project-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest(`/project-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => apiRequest(`/project-categories/${id}`, {
    method: 'DELETE',
  }),
};

export const headerApi = {
  get: () => apiRequest('/header'),
  update: (data: unknown) => apiRequest('/header', { method: 'PUT', body: JSON.stringify(data) }),
  upload: (field: 'logo_light'|'logo_dark'|'favicon', file: File) => {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/header/upload?field=${field}`, { method: 'POST', body: form, headers }).then(r => r.json());
  },
  export: () => apiRequest('/header/export'),
  import: (payload: unknown) => apiRequest('/header/import', { method: 'POST', body: JSON.stringify(payload) })
};

export const menuApi = {
  list: (type: 'header'|'footer'|'mobile' = 'header') => apiRequest(`/menu/items?type=${type}`),
  create: (data: unknown) => apiRequest('/menu/items', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: unknown) => apiRequest(`/menu/items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => apiRequest(`/menu/items/${id}`, { method: 'DELETE' }),
  reorder: (updates: {id: number; order: number; parent_id?: number|null}[]) => apiRequest('/menu/items/order', { method: 'PUT', body: JSON.stringify({ updates }) })
};

// Footer API
export const footerApi = {
  getSettings: () => apiRequest('/footer/settings'),
  updateSettings: (data: unknown) => apiRequest('/footer/settings', { method: 'PUT', body: JSON.stringify(data) }),
  upload: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/footer/upload`, { method: 'POST', body: form, headers });
    return await res.json();
  }
};

export const errorPageApi = {
  getSettings: async () => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_BASE}/error-page/settings`, { headers, cache: 'no-store' });
      if (res.status === 404) return { success: false, data: null };
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error ${res.status}: ${text}`);
      }
      return await res.json();
    } catch (e) {
      return { success: false, data: null, error: String(e instanceof Error ? e.message : e) } as { success: false; data: null; error: string };
    }
  },
  updateSettings: async (data: unknown) => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_BASE}/error-page/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
        cache: 'no-store'
      });
      if (res.status === 404) return { success: false, data: null };
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error ${res.status}: ${text}`);
      }
      return await res.json();
    } catch (e) {
      return { success: false, data: null, error: String(e instanceof Error ? e.message : e) } as { success: false; data: null; error: string };
    }
  },
  uploadBackground: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/error-page/upload-background`, { method: 'POST', body: form, headers });
    return await res.json();
  }
};

// Contact API
export const contactApi = {
  // Messages/Submissions
  getAll: () => apiRequest('/contact'),
  getStats: () => apiRequest('/contact/stats'),
  submit: (data: unknown) => apiRequest('/contact/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateStatus: (id: number, status: 'new'|'read'|'replied'|'archived') => apiRequest(`/contact/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
  delete: (id: number) => apiRequest(`/contact/${id}`, {
    method: 'DELETE',
  }),
  deleteMany: (ids: number[]) => apiRequest('/contact/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  }),

  // Settings & Fields
  getSettings: () => apiRequest('/contact/settings'),
  updateSettings: (data: unknown) => apiRequest('/contact/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getFields: () => apiRequest('/contact/fields'),
  addField: (data: unknown) => apiRequest('/contact/fields', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateField: (id: number | string, data: unknown) => apiRequest(`/contact/fields/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteField: (id: number | string) => apiRequest(`/contact/fields/${id}`, {
    method: 'DELETE',
  }),
  reorderFields: (orderUpdates: { id: number | string, order: number }[]) => apiRequest('/contact/fields/reorder', {
    method: 'PUT',
    body: JSON.stringify({ orderUpdates }),
  }),
  testEmail: (data: unknown) => apiRequest('/contact/test-email', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // Public config for the contact form
  getPublicSettings: () => apiRequest('/contact/public/settings'),
  getPublicFields: () => apiRequest('/contact/public/fields'),
};

export const pagesApi = {
  getAll: () => apiRequest('/pages'),
  getById: (id: string | number) => apiRequest(`/pages/${id}`),
  getBySlug: (slug: string) => apiRequest(`/pages/slug/${slug}`),
  create: (data: unknown) => apiRequest('/pages', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string | number, data: unknown) => apiRequest(`/pages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string | number) => apiRequest(`/pages/${id}`, { method: 'DELETE' }),
};

// Auth API
export const authApi = {
  login: (email: string, password: string) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  getProfile: () => apiRequest('/auth/profile'),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('👋 User logged out');
  },
  updateProfile: (data: { email?: string; password?: string }) => apiRequest('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Stats API
export const statsApi = {
  getDashboardStats: () => apiRequest('/stats/dashboard'),
};

export const themeApi = {
  get: async () => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_BASE}/theme`, { headers });
      if (res.status === 404) return { success: false, data: null };
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error ${res.status}: ${text}`);
      }
      return await res.json();
    } catch (e) {
      return { success: false, data: null, error: String(e instanceof Error ? e.message : e) } as { success: false; data: null; error: string };
    }
  },
  update: (data: unknown) => apiRequest('/theme', { method: 'PUT', body: JSON.stringify(data) }),
  reset: () => apiRequest('/theme/reset', { method: 'POST' }),
  cssGet: () => apiRequest('/theme/css'),
  cssUpdate: (css: string, minify = false) => apiRequest('/theme/css', { method: 'PUT', body: JSON.stringify({ css, minify }) }),
  presetsList: () => apiRequest('/theme/presets'),
  presetsSave: (name: string, data: unknown) => apiRequest('/theme/presets', { method: 'POST', body: JSON.stringify({ name, data }) }),
  export: () => apiRequest('/theme/export'),
  import: (payload: unknown) => apiRequest('/theme/import', { method: 'POST', body: JSON.stringify(payload) })
};

// Section Headings API
export const sectionApi = {
  get: (section: 'about' | 'services' | 'portfolio' | 'hero') => apiRequest(`/section_headings/${section}`),
  update: (section: 'about' | 'services' | 'portfolio' | 'hero', data: { heading?: string; description?: string; cta_heading?: string; cta_description?: string; badge_text?: string; stat1_label?: string; stat1_value?: string; stat2_label?: string; stat2_value?: string; stat3_label?: string; stat3_value?: string; button_text?: string; button_action?: 'modal' | 'link'; button_link?: string | null; secondary_button_text?: string; secondary_button_action?: 'modal' | 'link'; secondary_button_link?: string | null; portfolio_page_heading?: string; portfolio_page_description?: string; layout_style?: 'style1' | 'style2' | 'style3' | 'style4' | 'style5'; hero_background_type?: 'none' | 'image' | 'video'; hero_background_media?: string | null; hero_background_fit?: 'fit' | 'cover' }) => apiRequest(`/section_headings/${section}` , { method: 'PUT', body: JSON.stringify(data) }),
  uploadMedia: async (section: 'hero', file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await fetch(`${API_BASE}/section_headings/${section}/media`, {
      method: 'POST',
      headers,
      body: formData
    });
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(res.ok ? 'Invalid upload response from server' : `Upload failed (${res.status})`);
    }
    if (!res.ok) {
      throw new Error(data?.message || `Upload failed (${res.status})`);
    }
    return data;
  },
};

export const worksSectionApi = {
  get: () => apiRequest('/works-section'),
  update: (data: {
    badge_text?: string;
    heading?: string;
    description?: string;
    stat1_label?: string;
    stat1_value?: string;
    stat2_label?: string;
    stat2_value?: string;
    stat3_label?: string;
    stat3_value?: string;
  }) => apiRequest('/works-section', { method: 'PUT', body: JSON.stringify(data) }),
};

export const siteHeaderApi = {
  get: () => apiRequest('/header-settings'),
  create: (data: unknown) => apiRequest('/header-settings', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: { id?: number } & Record<string, unknown>) => {
    if (data && typeof data.id === 'number') {
      return apiRequest(`/header-settings/${data.id}`, { method: 'PUT', body: JSON.stringify(data) });
    }
    return apiRequest('/header-settings', { method: 'PUT', body: JSON.stringify(data) });
  },
  updateById: (id: number, data: unknown) => apiRequest(`/header-settings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/upload/logo`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return res.json();
  },
  
  getMenuItems: () => apiRequest('/header-menu-items'),
  addMenuItem: (data: unknown) => apiRequest('/header-menu-items', { method: 'POST', body: JSON.stringify(data) }),
  updateMenuItem: (id: number, data: unknown) => apiRequest(`/header-menu-items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMenuItem: (id: number) => apiRequest(`/header-menu-items/${id}`, { method: 'DELETE' }),
  reorderMenuItems: (orderUpdates: {id: number, order: number}[]) => apiRequest('/header-menu-items/reorder', { method: 'POST', body: JSON.stringify({ orderUpdates }) }),
};

export const preloaderApi = {
  getSettings: () => apiRequest('/preloader'),
  updateSettings: (data: unknown) => apiRequest('/preloader', { method: 'PUT', body: JSON.stringify(data) }),
  uploadSvg: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    return fetch(`${API_BASE}/preloader/upload`, { method: 'POST', body: formData, headers }).then(r => r.json());
  },
};

export const portfolioApi = {
  getAll: () => apiRequest('/portfolio'),
  getById: (id: string) => apiRequest(`/portfolio/${id}`),
  create: (data: unknown) => apiRequest('/portfolio', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: unknown) => apiRequest(`/portfolio/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/portfolio/${id}`, { method: 'DELETE' }),
  uploadTempImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/portfolio/upload-temp`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return res.json();
  },
  uploadTempGallery: async (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/portfolio/gallery-temp`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return res.json();
  },
  uploadImage: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/portfolio/${id}/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return res.json();
  },
  uploadGallery: async (id: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/portfolio/${id}/gallery`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return res.json();
  },
};

export default {
  heroApi,
  aboutApi,
  aboutExtendedApi,
  aboutPageContentApi,
  serviceApi,
  serviceCategoryApi,
  projectApi,
  contactApi,
  pagesApi,
  authApi,
  statsApi,
  themeApi,
  sectionApi,
  worksSectionApi,
  siteHeaderApi,
  footerApi,
  errorPageApi,
  preloaderApi,
  portfolioApi,
};
